import { useEffect, useState } from "react";
import { Plus, Trash2, ClipboardList } from "lucide-react";
import { RESOURCES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/TimeInput";
import { SuggestStaffButton } from "@/components/SuggestStaff";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const toMin = (s) => { const [h, m] = (s || "00:00").split(":").map(Number); return h * 60 + m; };
const toHm = (m) => { const x = ((m % 1440) + 1440) % 1440; return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };

// turno tipo: de dos horas antes del inicio a una hora después del final del evento
export const shiftWindow = (ev) => ({ start: toHm(toMin(ev.start_time) - 120), end: toHm(toMin(ev.end_time) + 60) });

export const defaultShifts = (ev) =>
  RESOURCES.filter((r) => ev.resources?.[r.key]).map((r) => ({
    id: crypto.randomUUID(), type: r.key, count: ev.resources[r.key], ...shiftWindow(ev), label: "",
  }));

export const totalsFromShifts = (shifts) =>
  Object.fromEntries(RESOURCES.map((r) => [r.key, shifts.filter((s) => s.type === r.key).reduce((a, s) => a + (Number(s.count) || 0), 0)]));

const ShiftRow = ({ s, onChange, onDelete, readOnly, idx }) => {
  const set = (k) => (e) => onChange({ ...s, [k]: e?.target ? (k === "count" ? Math.max(0, Number(e.target.value)) : e.target.value) : e });
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 rise" data-testid={`shift-row-${idx}`}>
      <div className="flex gap-2">
        <Select value={s.type} onValueChange={set("type")} disabled={readOnly}>
          <SelectTrigger className="h-9 flex-1 text-xs" data-testid={`shift-type-select-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>{RESOURCES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" min="0" value={s.count} onChange={set("count")} disabled={readOnly} className="w-16 h-9 text-center font-mono" data-testid={`shift-count-input-${idx}`} title="Nº de efectivos / patrullas" />
        {!readOnly && <Button size="sm" variant="ghost" className="h-9 px-2 hover:text-red-700" onClick={onDelete} data-testid={`shift-delete-button-${idx}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
      <div className="flex gap-2 items-center">
        <span className="text-[0.625rem] text-slate-500 font-mono">ENTRA</span>
        <TimeInput value={s.start} onChange={set("start")} disabled={readOnly} className="h-8 w-[84px] text-xs" data-testid={`shift-start-input-${idx}`} />
        <span className="text-[0.625rem] text-slate-500 font-mono">SALE</span>
        <TimeInput value={s.end} onChange={set("end")} disabled={readOnly} className="h-8 w-[84px] text-xs" data-testid={`shift-end-input-${idx}`} />
      </div>
      <Input value={s.label || ""} onChange={set("label")} disabled={readOnly} placeholder="Nota: turno, indicativo, entra tarde…" className="h-8 text-xs" data-testid={`shift-label-input-${idx}`} />
      <TipsInput s={s} idx={idx} readOnly={readOnly} onChange={(tips) => onChange({ ...s, tips })} />
    </div>
  );
};

const PATROLS = ["patrullas_coche", "patrullas_moto"];

const PatrolTips = ({ s, idx, readOnly, onChange }) => {
  const n = Math.max(0, Number(s.count) || 0);
  const tips = Array.from({ length: n }, (_, i) => (s.tips || [])[i] || "");
  const [vals, setVals] = useState(tips);
  useEffect(() => setVals(tips), [s.tips, n]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = tips.filter((t) => t.trim()).length;
  return (
    <div className="space-y-1.5" data-testid={`shift-patrol-tips-${idx}`}>
      <div className="flex justify-between text-[0.625rem] font-mono">
        <span className="text-amber-600">INDICATIVOS / TIP POR PATRULLA</span>
        <span className={n > 0 && done === n ? "text-emerald-600" : "text-slate-500"} data-testid={`shift-tips-count-${idx}`}>{done}/{n}</span>
      </div>
      {vals.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-[0.625rem] text-slate-500 font-mono w-16 shrink-0">PATRULLA {i + 1}</span>
          <Input value={v} disabled={readOnly} placeholder={s.type === "patrullas_coche" ? "12001/12056" : "12001"}
            onChange={(e) => setVals(vals.map((x, j) => (j === i ? e.target.value : x)))}
            onBlur={() => onChange(vals.map((x) => x.trim()))}
            className="h-8 text-xs font-mono" data-testid={`shift-patrol-tip-input-${idx}-${i}`} />
        </div>
      ))}
    </div>
  );
};

const TipsInput = ({ s, idx, readOnly, onChange }) => {
  const [txt, setTxt] = useState((s.tips || []).join(", "));
  useEffect(() => setTxt((s.tips || []).join(", ")), [s.tips]);
  const n = (s.tips || []).filter(Boolean).length;
  if (PATROLS.includes(s.type)) return <PatrolTips s={s} idx={idx} readOnly={readOnly} onChange={onChange} />;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[0.625rem] text-amber-600 font-mono shrink-0">TIP</span>
      <Input value={txt} disabled={readOnly} onChange={(e) => setTxt(e.target.value)}
        onBlur={() => onChange(txt.split(",").map((x) => x.trim()).filter(Boolean))}
        placeholder="Separados por coma: 12045, 12078…"
        className="h-8 text-xs font-mono" data-testid={`shift-tips-input-${idx}`} />
      <span className={`font-mono text-[0.625rem] shrink-0 ${n === Number(s.count) ? "text-emerald-600" : n > Number(s.count) ? "text-red-600" : "text-slate-500"}`} title={n > Number(s.count) ? "Hay más TIP que efectivos: los sobrantes se ignoran" : ""} data-testid={`shift-tips-count-${idx}`}>{n}/{s.count}</span>
    </div>
  );
};

export const ShiftsBriefing = ({ shifts, onChange, readOnly, summary, isNew, eventId, window: win }) => {
  const used = Object.fromEntries((summary?.by_type || []).map((b) => [b.type, b.used]));
  const totals = totalsFromShifts(shifts);
  const add = () => onChange([...shifts, { id: crypto.randomUUID(), type: "policia_pie", count: 1, start: shifts[0]?.start || win?.start || "18:00", end: shifts[0]?.end || win?.end || "22:00", label: "" }]);
  return (
    <div className="space-y-3" data-testid="shifts-briefing">
      <div className={`flex gap-3 rounded-lg p-3 text-xs ${isNew ? "bg-amber-500/10 border border-amber-500/30 text-amber-800" : "bg-sky-500/5 border border-sky-500/20 text-slate-700"}`} data-testid="briefing-hint">
        <ClipboardList className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Indique cada grupo de personal con su hora de <b>entrada</b> y <b>salida</b>. Si un turno sale antes de terminar un corte, el plan asigna el <b>relevo</b> con el turno entrante; quien entra tarde solo se usa desde su hora.</span>
      </div>
      <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1 pb-3">
        {shifts.map((s, i) => (
          <ShiftRow key={s.id} s={s} idx={i} readOnly={readOnly}
            onChange={(n) => onChange(shifts.map((x) => (x.id === s.id ? n : x)))}
            onDelete={() => onChange(shifts.filter((x) => x.id !== s.id))} />
        ))}
      </div>
      {!readOnly && <Button variant="outline" className="w-full" onClick={add} data-testid="add-shift-button"><Plus className="w-4 h-4 mr-1" /> Añadir grupo / turno</Button>}
      {!readOnly && eventId && <SuggestStaffButton eventId={eventId} onApply={(s, mode) => onChange(mode === "replace" ? s : [...shifts, ...s])} />}
      <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-slate-200" data-testid="shift-totals">
        {RESOURCES.map((r) => (
          <div key={r.key} className="flex justify-between text-[0.6875rem] text-slate-500 bg-slate-50 rounded px-2 py-1">
            <span className="truncate">{r.label.replace(" (a pie)", "")}</span>
            <span className="font-mono text-slate-800">{summary ? `${used[r.key] ?? 0}/` : ""}{totals[r.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
